import { useState } from "react";
import { api } from "../../lib/api";
import { WHATSAPP_LINK, COMPANY } from "../../lib/constants";
import { trackEvent } from "../../lib/api";
import { CalendarClock, MessageCircle, CheckCircle2, Sun, Sunrise, Moon, ChevronRight } from "lucide-react";

const TIMES = [
  { key: "ochtend", label: "Ochtend", sub: "08:00 – 12:00", Icon: Sunrise },
  { key: "middag", label: "Middag", sub: "12:00 – 17:00", Icon: Sun },
  { key: "avond", label: "Avond", sub: "17:00 – 20:00", Icon: Moon },
];

// Minimum selectable date = day after tomorrow (geef bezoeker realistische verwachting)
const minDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().split("T")[0];
};

export const InmetingReservering = ({ bron = "/montage", defaultWoonplaats = "" }) => {
  const [form, setForm] = useState({
    naam: "",
    telefoon: "",
    email: "",
    woonplaats: defaultWoonplaats,
    voorkeur_datum: "",
    voorkeur_tijd: "",
    opmerking: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.naam || !form.telefoon || !form.woonplaats || !form.voorkeur_datum || !form.voorkeur_tijd) {
      setError("Vul naam, telefoon, woonplaats, voorkeursdatum en dagdeel in.");
      return;
    }
    setSubmitting(true);
    try {
      const datumStr = new Date(form.voorkeur_datum).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      const tijdLabel = TIMES.find((t) => t.key === form.voorkeur_tijd)?.label || form.voorkeur_tijd;
      const bericht = [
        `📅 Voorkeur inmeetdatum: ${datumStr}`,
        `⏰ Dagdeel: ${tijdLabel}`,
        form.opmerking ? `\nOpmerkingen: ${form.opmerking}` : "",
      ].filter(Boolean).join("\n");

      await api.post("/leads", {
        naam: form.naam.trim(),
        telefoon: form.telefoon.trim(),
        email: (form.email || "geen@maaskozijn.nl").trim(),
        woonplaats: form.woonplaats.trim(),
        type_aanvraag: "gratis-inmeting",
        uitvoerperiode: form.voorkeur_datum,
        bericht,
        bron_pagina: bron,
      });
      trackEvent("inmeting_reservering_submit", { woonplaats: form.woonplaats });
      setSuccess(true);
    } catch (err) {
      setError("Er ging iets mis bij het versturen. Bel of WhatsApp ons direct — dan plannen we samen een moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section id="inmeting" className="section bg-gradient-to-b from-white to-slate-50 border-y border-slate-100">
        <div className="container-tight">
          <div data-testid="inmeting-success" className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.15)] text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-5">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-semibold text-slate-900">Uw inmeting is voorgesteld</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              We hebben uw voorkeur ontvangen. Binnen 1 werkdag krijgt u een bevestiging per telefoon of WhatsApp op <strong>{form.telefoon}</strong> — met de definitieve datum en tijd.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                data-testid="inmeting-success-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-whatsapp hover:bg-whatsapp-hover text-white font-semibold py-3.5 px-6 text-[15px] transition-all hover:-translate-y-0.5 shadow-sm shadow-whatsapp/25"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp bevestiging vragen
              </a>
              <a
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-slate-200 hover:border-maas text-slate-800 hover:text-maas font-semibold py-3.5 px-6 text-[15px] transition-colors"
              >
                Bel {COMPANY.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="inmeting" className="section bg-gradient-to-b from-white to-slate-50 border-y border-slate-100">
      <div className="container-tight">
        <div className="max-w-4xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start">
          {/* Left — copy */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-maas-soft text-maas-deep text-xs font-semibold uppercase tracking-wide px-3 py-1.5">
              <CalendarClock className="h-3.5 w-3.5" /> Gratis inmeting
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-display font-semibold text-slate-900 leading-tight">
              Reserveer een gratis inmeting op locatie
            </h2>
            <p className="mt-4 text-slate-600 text-[15.5px] leading-relaxed">
              Geen verplichting, geen kosten. Onze inmeter komt langs, meet uw kozijnen, bespreekt uw wensen en beantwoordt uw vragen. U ontvangt binnen 3 werkdagen een <strong>heldere offerte</strong> — of niet, dat kiest u zelf.
            </p>
            <ul className="mt-6 space-y-2.5 text-[14.5px] text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-maas shrink-0 mt-0.5" /> Duurt ongeveer 30 – 45 minuten</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-maas shrink-0 mt-0.5" /> Advies over profielen, glas, kleur en beslag</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-maas shrink-0 mt-0.5" /> Realistische prijsindicatie tijdens het bezoek</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-5 w-5 text-maas shrink-0 mt-0.5" /> Geen verplichting, u kiest zelf hoe u verder gaat</li>
            </ul>
            <p className="mt-6 text-xs text-slate-500">
              Snelle voorkeur via WhatsApp? <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="text-maas hover:text-maas-deep font-medium underline underline-offset-2">Stuur ons uw voorkeursdatum</a>.
            </p>
          </div>

          {/* Right — form card */}
          <form
            onSubmit={submit}
            data-testid="inmeting-form"
            className="bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.15)] p-6 sm:p-8"
          >
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Naam*</span>
                  <input
                    type="text"
                    data-testid="inmeting-input-naam"
                    value={form.naam}
                    onChange={setField("naam")}
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition"
                    placeholder="Uw naam"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Telefoon*</span>
                  <input
                    type="tel"
                    data-testid="inmeting-input-telefoon"
                    value={form.telefoon}
                    onChange={setField("telefoon")}
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition"
                    placeholder="06 - 12345678"
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Woonplaats*</span>
                  <input
                    type="text"
                    data-testid="inmeting-input-woonplaats"
                    value={form.woonplaats}
                    onChange={setField("woonplaats")}
                    required
                    className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition"
                    placeholder="Maassluis, Vlaardingen, Rotterdam..."
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">E-mail <span className="text-slate-400 font-normal normal-case tracking-normal">(optioneel)</span></span>
                  <input
                    type="email"
                    data-testid="inmeting-input-email"
                    value={form.email}
                    onChange={setField("email")}
                    className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition"
                    placeholder="uw@email.nl"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Voorkeursdatum*</span>
                <input
                  type="date"
                  data-testid="inmeting-input-datum"
                  value={form.voorkeur_datum}
                  min={minDate()}
                  onChange={setField("voorkeur_datum")}
                  required
                  className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition"
                />
                <span className="mt-1.5 block text-[11.5px] text-slate-500">We nemen contact op om de exacte tijd te bevestigen. Minimaal 2 werkdagen vooruit.</span>
              </label>

              <div>
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Voorkeur dagdeel*</span>
                <div className="mt-2 grid grid-cols-3 gap-2.5">
                  {TIMES.map((t) => {
                    const active = form.voorkeur_tijd === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`inmeting-tijd-${t.key}`}
                        onClick={() => setForm({ ...form, voorkeur_tijd: t.key })}
                        className={`group flex flex-col items-center gap-1 rounded-xl border-2 py-3 px-2 text-center transition-all ${
                          active
                            ? "border-maas bg-maas-soft/50 text-maas-deep shadow-sm"
                            : "border-slate-200 hover:border-maas/40 text-slate-700"
                        }`}
                      >
                        <t.Icon className={`h-5 w-5 ${active ? "text-maas" : "text-slate-400 group-hover:text-maas/60"} transition-colors`} />
                        <span className="text-[13.5px] font-semibold">{t.label}</span>
                        <span className="text-[11px] text-slate-500">{t.sub}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Opmerkingen <span className="text-slate-400 font-normal normal-case tracking-normal">(optioneel)</span></span>
                <textarea
                  data-testid="inmeting-input-opmerking"
                  value={form.opmerking}
                  onChange={setField("opmerking")}
                  rows={2}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 focus:border-maas focus:ring-2 focus:ring-maas/20 px-4 py-3 text-[15px] outline-none transition resize-none"
                  placeholder="Bijv. hoeveel kozijnen, verdieping, bijzonderheden..."
                />
              </label>

              {error && (
                <p data-testid="inmeting-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="inmeting-submit"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold hover:bg-gold-hover text-white font-semibold py-4 px-6 text-[15.5px] transition-all hover:-translate-y-0.5 hover:shadow-lg shadow-gold/20 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
              >
                {submitting ? "Bezig met versturen..." : "Reserveer gratis inmeting"}
                {!submitting && <ChevronRight className="h-4 w-4" />}
              </button>
              <p className="text-center text-[11.5px] text-slate-500">
                Geen verplichting. Wij nemen binnen 1 werkdag contact op om de afspraak te bevestigen.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
